/**
 * Unterlagen prüfen — die Tabelle aus der Vorlage.
 *
 * Spalten: Dokument, Bereich, Datum, Prüfstatus. Darüber die Filter nach
 * Bereich (den fünf Schritten) und Status. Leere Kategorien erscheinen
 * ebenfalls, als „noch nicht bereitgestellt": Der Beirat soll sehen, was
 * fehlt, nicht nur, was da ist.
 */

import { useMemo, useState } from 'react';

import { datum } from '../../lib/api';
import type { Dokument, Kategorie, PruefStatus, Schritt } from '../../lib/typen';
import { groesseText } from '../../komponenten/Dokumentansicht';

export interface UnterlagenListeProps {
  kategorien: Kategorie[];
  dokumente: Dokument[];
  schritte: Schritt[];
  /** Nur die Unterlagen eines Schritts zeigen — in der Schrittansicht. */
  nurSchritt?: number;
  onOeffnen: (dokumentId: string) => void;
  /** Ohne Filterleiste und Überschrift, für die Schrittansicht. */
  schlicht?: boolean;
}

export function UnterlagenListe({ kategorien, dokumente, schritte, nurSchritt, onOeffnen, schlicht }: UnterlagenListeProps) {
  const [bereichWahl, setBereich] = useState<number | 'alle'>('alle');
  const [status, setStatus] = useState<PruefStatus | 'alle'>('alle');
  // In der Schrittansicht zählt der Schritt, nicht die Filterwahl — die
  // Komponente bleibt beim Wechsel von Schritt 1 zu 2 bestehen.
  const bereich = nurSchritt ?? bereichWahl;

  const zeilen = useMemo(() => {
    const katNach = new Map(kategorien.map((k) => [k.id, k]));
    const gefiltert = dokumente
      .filter((d) => {
        const k = katNach.get(d.kategorieId);
        if (!k) return false;
        if (nurSchritt !== undefined && k.schritt !== nurSchritt) return false;
        if (bereich !== 'alle' && k.schritt !== bereich) return false;
        if (status !== 'alle' && d.pruefstatus !== status) return false;
        return true;
      })
      .map((d) => ({ d, k: katNach.get(d.kategorieId)! }))
      .sort((a, b) => a.k.schritt - b.k.schritt || a.k.reihenfolge - b.k.reihenfolge || a.d.dateiname.localeCompare(b.d.dateiname));
    return gefiltert;
  }, [kategorien, dokumente, bereich, status, nurSchritt]);

  return (
    <div>
      {!schlicht && (
        <div className="filterleiste">
          <div className="chips" role="group" aria-label="Bereich">
            <button className="fchip" aria-pressed={bereich === 'alle'} onClick={() => setBereich('alle')}>
              Alle Bereiche
            </button>
            {schritte.map((s) => (
              <button key={s.nummer} className="fchip" aria-pressed={bereich === s.nummer} onClick={() => setBereich(s.nummer)}>
                {s.nummer}. {s.titel}
              </button>
            ))}
          </div>
          <div className="chips" role="group" aria-label="Status">
            {(
              [
                ['alle', 'Alle Status'],
                ['offen', 'Noch offen'],
                ['geprueft', 'Geprüft'],
                ['rueckfrage', 'Rückfrage'],
              ] as const
            ).map(([wert, text]) => (
              <button key={wert} className="fchip" aria-pressed={status === wert} onClick={() => setStatus(wert)}>
                {text}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="tblwrap card unterlagen-karte">
        <table className="data unterlagen">
          <thead>
            <tr>
              <th>Dokument</th>
              <th>Bereich</th>
              <th>Datum</th>
              <th>Umfang</th>
              <th>Prüfstatus</th>
            </tr>
          </thead>
          <tbody>
            {zeilen.map(({ d, k }) => (
              <tr key={d.id} className="klickbar" onClick={() => onOeffnen(d.id)}>
                <td>
                  <div className="dok-zelle">
                    <span className={`dok-kachel ${dateiArt(d.typ)}`} aria-hidden="true">{dateiArt(d.typ).toUpperCase()}</span>
                    <span className="dok-zelle-text">
                      <b>{k.bezeichnung}</b>
                      <span className="s" style={{ margin: 0 }}>{d.dateiname}</span>
                    </span>
                  </div>
                </td>
                <td>
                  <span className="chip">Schritt {k.schritt}</span>
                </td>
                <td>{datum(d.hochgeladenAm)}</td>
                <td className="s" style={{ margin: 0 }}>
                  {d.seiten ? `${d.seiten} S. · ` : ''}
                  {groesseText(d.groesse)}
                </td>
                <td>
                  <span className={`chip${d.pruefstatus === 'geprueft' ? ' ok' : d.pruefstatus === 'rueckfrage' ? ' warn' : ''}`}>
                    {d.pruefstatus === 'geprueft' ? 'Geprüft' : d.pruefstatus === 'rueckfrage' ? 'Rückfrage' : 'Noch offen'}
                  </span>
                </td>
              </tr>
            ))}
            {zeilen.length === 0 && (
              <tr>
                <td colSpan={5} className="s" style={{ textAlign: 'center', padding: 24 }}>
                  {nurSchritt !== undefined ? 'Zu diesem Schritt wurde noch nichts bereitgestellt.' : 'Keine Unterlagen in dieser Auswahl.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function dateiArt(typ: string): 'pdf' | 'bild' | 'tabelle' | 'datei' {
  if (typ === 'application/pdf') return 'pdf';
  if (typ.startsWith('image/')) return 'bild';
  if (typ.includes('csv') || typ.includes('excel') || typ.includes('spreadsheet')) return 'tabelle';
  return 'datei';
}
